import { useEffect, useRef } from "react";
import { MapContainer, TileLayer, useMap } from "react-leaflet";
import L from "leaflet";
import "leaflet/dist/leaflet.css";
import "leaflet.markercluster/dist/MarkerCluster.css";
import "leaflet.markercluster/dist/MarkerCluster.Default.css";
import "leaflet.markercluster";
import { imageUrl } from "@/lib/api";
import { useI18n } from "@/context/LangContext";
import { pickLocalized, labelType } from "@/lib/i18n";

const KUCHING = [1.5555, 110.3452];

const makePin = (label = "") =>
  L.divIcon({
    html: `<div class="heritage-pin">${label}</div>`,
    className: "",
    iconSize: [28, 28],
    iconAnchor: [14, 14],
    popupAnchor: [0, -14],
  });

const makeClusterIcon = (cluster) => {
  const count = cluster.getChildCount();
  const size = count < 10 ? 40 : count < 50 ? 48 : 56;
  return L.divIcon({
    html: `<div class="heritage-cluster" style="width:${size}px;height:${size}px;line-height:${size}px;">${count}</div>`,
    className: "",
    iconSize: L.point(size, size),
  });
};

function popupHtml(it, backendUrl, lang, viewLabel) {
  const img = it.image_urls?.[0] || it.image_url;
  const src = img
    ? img.startsWith("http")
      ? img
      : `${backendUrl}${img}`
    : "";
  const imgHtml = src
    ? `<div style="aspect-ratio:16/9;overflow:hidden;background:#efece4"><img src="${src}" alt="" style="width:100%;height:100%;object-fit:cover" /></div>`
    : "";
  const name = pickLocalized(it, "name", lang);
  const description = pickLocalized(it, "description", lang);
  const typeLabel = labelType(it.type, lang);
  const desc = String(description || "").replace(/</g, "&lt;");
  const nm = String(name || "").replace(/</g, "&lt;");
  return `
    <div style="width:100%">
      ${imgHtml}
      <div style="padding:12px">
        <div style="font-size:10px;text-transform:uppercase;letter-spacing:.2em;font-weight:600;color:#5c5c5c">${typeLabel}</div>
        <div style="font-family:'Playfair Display',serif;font-weight:700;font-size:16px;color:#1a1a1a;margin-top:2px">${nm}</div>
        <div style="font-size:12px;color:#5c5c5c;margin-top:4px;display:-webkit-box;-webkit-line-clamp:2;-webkit-box-orient:vertical;overflow:hidden">${desc}</div>
        <button data-open-heritage="${it.id}" style="margin-top:10px;background:none;border:0;color:#b25a38;font-size:12px;font-weight:500;cursor:pointer;letter-spacing:.02em;padding:0">${viewLabel} →</button>
      </div>
    </div>`;
}

function ClusterLayer({ items, onOpen, lang, viewLabel }) {
  const map = useMap();
  const groupRef = useRef(null);

  useEffect(() => {
    const group = L.markerClusterGroup({
      showCoverageOnHover: false,
      spiderfyOnMaxZoom: true,
      maxClusterRadius: 55,
      iconCreateFunction: makeClusterIcon,
    });
    groupRef.current = group;
    map.addLayer(group);
    return () => {
      map.removeLayer(group);
      groupRef.current = null;
    };
  }, [map]);

  useEffect(() => {
    const group = groupRef.current;
    if (!group) return;
    group.clearLayers();
    const backendUrl = process.env.REACT_APP_BACKEND_URL;
    const valid = items.filter(
      (i) => typeof i.latitude === "number" && typeof i.longitude === "number"
    );
    valid.forEach((it, idx) => {
      const marker = L.marker([it.latitude, it.longitude], {
        icon: makePin(String(idx + 1)),
      });
      marker.bindPopup(popupHtml(it, backendUrl, lang, viewLabel), { maxWidth: 280 });
      marker.on("popupopen", (e) => {
        const el = e.popup.getElement();
        const btn = el?.querySelector("[data-open-heritage]");
        if (btn) {
          btn.onclick = () => onOpen?.(it);
        }
      });
      group.addLayer(marker);
    });
    if (valid.length > 0) {
      const bounds = L.latLngBounds(valid.map((v) => [v.latitude, v.longitude]));
      if (valid.length === 1) map.setView(bounds.getCenter(), 15);
      else map.fitBounds(bounds, { padding: [40, 40] });
    }
  }, [items, onOpen, map, lang, viewLabel]);

  return null;
}

export default function HeritageMap({ items, onOpen }) {
  const { lang, t } = useI18n();
  const viewLabel = lang === "ms" ? "Lihat butiran" : "View details";
  return (
    <div
      className="border border-[#e5e3db] bg-white"
      style={{ height: "600px" }}
      data-testid="heritage-map"
    >
      <MapContainer
        center={KUCHING}
        zoom={13}
        scrollWheelZoom
        style={{ height: "100%", width: "100%" }}
      >
        <TileLayer
          attribution='&copy; OpenStreetMap contributors &copy; CARTO'
          url="https://{s}.basemaps.cartocdn.com/light_all/{z}/{x}/{y}{r}.png"
        />
        <ClusterLayer items={items} onOpen={onOpen} lang={lang} viewLabel={viewLabel} />
      </MapContainer>
    </div>
  );
}
